import { useEffect, useRef, useState } from 'react';
import { EditorContent, useEditor, useEditorState, type Editor } from '@tiptap/react';
import { Document } from '@tiptap/extension-document';
import { Paragraph } from '@tiptap/extension-paragraph';
import { Text } from '@tiptap/extension-text';
import { Bold } from '@tiptap/extension-bold';
import { Italic } from '@tiptap/extension-italic';
import { HardBreak } from '@tiptap/extension-hard-break';
import { Link } from '@tiptap/extension-link';
import { FontSize, TextStyle } from '@tiptap/extension-text-style';
import { Placeholder, UndoRedo } from '@tiptap/extensions';
import {
  Bold as BoldIcon, ExternalLink, Italic as ItalicIcon, Link2, Pencil, Redo2, RemoveFormatting, Undo2, Unlink,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Popover, PopoverAnchor, PopoverContent } from '@/components/ui/popover';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Separator } from '@/components/ui/separator';
import { Toggle } from '@/components/ui/toggle';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import { cn } from '@/lib/utils';
import { RICH_SIZES, normalizeLinkInput, richLength, safeHref, type RichText } from '@/lib/coach-guide';
import { docToRuns, runsToDoc } from '@/lib/coach-guide-rich';
import { EmojiButton } from './EmojiPicker';

const MOD = typeof navigator !== 'undefined' && /Mac|iPhone|iPad/.test(navigator.platform) ? '⌘' : 'Ctrl';

// Only what the stored runs can hold (see lib/coach-guide-rich.ts), so pasted
// headings, lists, colours etc. are reduced to plain paragraphs.
// A single-line field is one paragraph with no hard breaks.
const extensions = (placeholder: string, singleLine: boolean) => [
  singleLine ? Document.extend({ content: 'paragraph' }) : Document,
  Paragraph,
  Text,
  ...(singleLine ? [] : [HardBreak]),
  Bold,
  Italic,
  TextStyle,
  FontSize,
  UndoRedo,
  Placeholder.configure({ placeholder }),
  // Not inclusive: typing right after a link doesn't extend it.
  Link.extend({ inclusive: () => false }).configure({
    openOnClick: false,
    autolink: true,
    linkOnPaste: true,
    defaultProtocol: 'https',
    protocols: ['mailto'],
    isAllowedUri: (url) => !!safeHref(normalizeLinkInput(url)),
    HTMLAttributes: { target: '_blank', rel: 'noopener noreferrer', class: null },
  }),
];

function ToolButton({ label, shortcut, pressed, disabled, onClick, children }: {
  label: string; shortcut?: string; pressed?: boolean; disabled?: boolean; onClick: () => void; children: React.ReactNode;
}) {
  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <Toggle
          size="sm"
          className="h-7 w-7 p-0"
          pressed={!!pressed}
          disabled={disabled}
          onPressedChange={onClick}
          // Keep the editor's selection while clicking toolbar buttons.
          onMouseDown={(e) => e.preventDefault()}
          aria-label={label}
        >
          {children}
        </Toggle>
      </TooltipTrigger>
      <TooltipContent>{label}{shortcut ? ` (${shortcut})` : ''}</TooltipContent>
    </Tooltip>
  );
}

/** Link editor popover: add a link to the selection, insert new linked text, or change/remove an existing link. */
function LinkPopover({ editor, open, onOpenChange, anchor }: {
  editor: Editor; open: boolean; onOpenChange: (open: boolean) => void; anchor: React.ReactNode;
}) {
  const [text, setText] = useState('');
  const [url, setUrl] = useState('');
  const [mode, setMode] = useState<{ needsText: boolean; editing: boolean }>({ needsText: false, editing: false });

  // Read the editor when the popover opens.
  useEffect(() => {
    if (!open) return;
    const editing = editor.isActive('link');
    const { empty } = editor.state.selection;
    setMode({ needsText: empty && !editing, editing });
    setUrl(editing ? String(editor.getAttributes('link').href ?? '') : '');
    setText('');
  }, [open, editor]);

  const href = safeHref(normalizeLinkInput(url));
  const canApply = !!href && (!mode.needsText || !!text.trim());

  const apply = () => {
    if (!href) return;
    if (mode.needsText) {
      editor.chain().focus().insertContent({ type: 'text', text: text.trim(), marks: [{ type: 'link', attrs: { href } }] }).run();
    } else {
      editor.chain().focus().extendMarkRange('link').setLink({ href }).run();
    }
    onOpenChange(false);
  };
  const remove = () => {
    editor.chain().focus().extendMarkRange('link').unsetLink().run();
    onOpenChange(false);
  };

  return (
    <Popover open={open} onOpenChange={onOpenChange}>
      <PopoverAnchor asChild>{anchor}</PopoverAnchor>
      <PopoverContent align="start" className="w-80 p-3" onCloseAutoFocus={(e) => { e.preventDefault(); editor.commands.focus(); }}>
        <form className="space-y-3" onSubmit={(e) => { e.preventDefault(); apply(); }}>
          <p className="text-sm font-medium">{mode.editing ? 'Edit link' : 'Add link'}</p>
          {mode.needsText && (
            <div className="space-y-1">
              <label className="text-xs font-medium">Text to show</label>
              <Input value={text} onChange={(e) => setText(e.target.value)} placeholder="e.g. Contact support" className="h-8" autoFocus />
            </div>
          )}
          <div className="space-y-1">
            <label className="text-xs font-medium">Link</label>
            <Input
              value={url}
              onChange={(e) => setUrl(e.target.value)}
              placeholder="Paste a web address or email"
              className="h-8"
              autoFocus={!mode.needsText}
            />
            {url.trim() && !href && <p className="text-[11px] text-destructive">That doesn’t look like a web address or email.</p>}
            {href && <p className="truncate text-[11px] text-muted-foreground">Opens {href}</p>}
          </div>
          <div className="flex items-center justify-between gap-2">
            {mode.editing ? (
              <Button type="button" variant="ghost" size="sm" className="h-8 px-2 text-destructive hover:text-destructive" onClick={remove}>
                <Unlink className="mr-1 h-3.5 w-3.5" /> Remove
              </Button>
            ) : <span />}
            <div className="flex gap-2">
              <Button type="button" variant="ghost" size="sm" className="h-8" onClick={() => onOpenChange(false)}>Cancel</Button>
              <Button type="submit" size="sm" className="h-8" disabled={!canApply}>Apply</Button>
            </div>
          </div>
        </form>
      </PopoverContent>
    </Popover>
  );
}

/**
 * Rich text field for the guide: bold, italic, links, emoji and font size,
 * edited in place like a document. Reads and writes the guide's stored runs.
 */
export function RichTextEditor({
  value,
  onChange,
  placeholder = '',
  baseSize,
  max,
  invalid,
  ariaLabel,
  minRows = 2,
  singleLine = false,
  compact = false,
}: {
  value: RichText;
  onChange: (value: RichText) => void;
  placeholder?: string;
  /** The size this text has when no size is picked (from the Style tab), shown in the size menu. */
  baseSize: number;
  /** Character limit, shown as a counter. */
  max?: number;
  invalid?: boolean;
  ariaLabel?: string;
  minRows?: number;
  /** Titles and labels: Enter does nothing and pasted lines are joined. */
  singleLine?: boolean;
  /** Show the toolbar only while editing, floating above the field (for long lists like prompts). */
  compact?: boolean;
}) {
  const onChangeRef = useRef(onChange);
  onChangeRef.current = onChange;
  const lastEmitted = useRef(JSON.stringify(value));
  const [linkOpen, setLinkOpen] = useState(false);
  const [emojiOpen, setEmojiOpen] = useState(false);
  const [sizeOpen, setSizeOpen] = useState(false);
  const [focused, setFocused] = useState(false);
  const openLinkRef = useRef(() => setLinkOpen(true));
  // Older single-line values could hold line breaks; show them as spaces.
  const toDoc = (runs: RichText) => runsToDoc(singleLine ? runs.map((r) => ({ ...r, text: r.text.replace(/\n+/g, ' ') })) : runs);

  const editor = useEditor({
    extensions: extensions(placeholder, singleLine),
    content: toDoc(value),
    editorProps: {
      attributes: {
        class: 'guide-editor-content',
        role: 'textbox',
        'aria-multiline': 'true',
        ...(ariaLabel ? { 'aria-label': ariaLabel } : {}),
      },
      handleKeyDown: (_view, event) => {
        if ((event.metaKey || event.ctrlKey) && event.key.toLowerCase() === 'k') {
          event.preventDefault();
          openLinkRef.current();
          return true;
        }
        return singleLine && event.key === 'Enter';
      },
      // Multi-line text pasted into a single-line field becomes one line.
      handlePaste: (view, event) => {
        if (!singleLine) return false;
        const text = event.clipboardData?.getData('text/plain') ?? '';
        if (!/\n/.test(text)) return false;
        view.dispatch(view.state.tr.insertText(text.replace(/\s*\n+\s*/g, ' ').trim()));
        return true;
      },
    },
    onUpdate: ({ editor: e }) => {
      const runs = docToRuns(e.getJSON());
      lastEmitted.current = JSON.stringify(runs);
      onChangeRef.current(runs);
    },
  });

  // Replace the content only when the value changed from outside (discard,
  // restore a version), never while typing, so the cursor stays put.
  useEffect(() => {
    if (!editor) return;
    const next = JSON.stringify(value);
    if (next === lastEmitted.current) return;
    lastEmitted.current = next;
    editor.commands.setContent(toDoc(value), { emitUpdate: false });
    // toDoc only depends on singleLine, which never changes for a field.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editor, value]);

  useEffect(() => {
    if (!editor) return;
    const onFocus = () => setFocused(true);
    const onBlur = () => setFocused(false);
    editor.on('focus', onFocus);
    editor.on('blur', onBlur);
    return () => {
      editor.off('focus', onFocus);
      editor.off('blur', onBlur);
    };
  }, [editor]);

  const state = useEditorState({
    editor,
    selector: ({ editor: e }) =>
      e
        ? {
            bold: e.isActive('bold'),
            italic: e.isActive('italic'),
            link: e.isActive('link'),
            href: (e.getAttributes('link').href as string | undefined) ?? '',
            fontSize: (e.getAttributes('textStyle').fontSize as string | undefined) ?? '',
            canUndo: e.can().undo(),
            canRedo: e.can().redo(),
          }
        : null,
  });

  if (!editor || !state) return null;

  const currentSize = state.fontSize ? String(parseFloat(state.fontSize)) : 'default';
  const sizes = currentSize !== 'default' && !RICH_SIZES.includes(Number(currentSize))
    ? [...RICH_SIZES, Number(currentSize)].sort((a, b) => a - b)
    : RICH_SIZES;
  const length = richLength(value);
  // Compact fields show the toolbar while editing, including while one of its menus has focus.
  const active = !compact || focused || linkOpen || emojiOpen || sizeOpen;
  const overLimit = max != null && length > max;

  const linkButton = (
    <span>
      <ToolButton label={state.link ? 'Edit link' : 'Add link'} shortcut={`${MOD}+K`} pressed={state.link} onClick={() => setLinkOpen(true)}>
        <Link2 className="h-3.5 w-3.5" />
      </ToolButton>
    </span>
  );

  return (
    <div
      className={cn(
        'guide-editor relative rounded-md border border-input bg-background ring-offset-background focus-within:ring-2 focus-within:ring-ring focus-within:ring-offset-2',
        !compact && 'overflow-hidden',
        invalid && 'border-destructive focus-within:ring-destructive',
      )}
      style={{ ['--guide-editor-min-h' as string]: `${minRows * 1.5 + 1}rem` }}
    >
      <div
        className={cn(
          'flex flex-wrap items-center gap-0.5 px-1.5 py-1',
          compact
            ? 'absolute bottom-full left-0 z-20 mb-1.5 rounded-md border bg-popover shadow-md'
            : 'border-b bg-muted/40',
          compact && !active && 'hidden',
        )}
        // Clicks on the floating toolbar's gaps shouldn't blur the field.
        onMouseDown={compact ? (e) => { if (e.target === e.currentTarget) e.preventDefault(); } : undefined}
      >
        <ToolButton label="Undo" shortcut={`${MOD}+Z`} disabled={!state.canUndo} onClick={() => editor.chain().focus().undo().run()}>
          <Undo2 className="h-3.5 w-3.5" />
        </ToolButton>
        <ToolButton label="Redo" shortcut={`${MOD}+Shift+Z`} disabled={!state.canRedo} onClick={() => editor.chain().focus().redo().run()}>
          <Redo2 className="h-3.5 w-3.5" />
        </ToolButton>
        <Separator orientation="vertical" className="mx-1 h-5" />

        <Select
          value={currentSize}
          onOpenChange={setSizeOpen}
          onValueChange={(v) => {
            if (v === 'default') editor.chain().focus().unsetFontSize().run();
            else editor.chain().focus().setFontSize(`${v}px`).run();
          }}
        >
          <Tooltip>
            <TooltipTrigger asChild>
              <SelectTrigger className="h-7 w-[104px] border-0 bg-transparent px-2 text-xs shadow-none focus:ring-0" aria-label="Font size">
                <SelectValue />
              </SelectTrigger>
            </TooltipTrigger>
            <TooltipContent>Font size of the selected text</TooltipContent>
          </Tooltip>
          <SelectContent onCloseAutoFocus={(e) => { e.preventDefault(); editor.commands.focus(); }}>
            <SelectItem value="default" className="text-xs">Default ({baseSize}px)</SelectItem>
            {sizes.map((px) => (
              <SelectItem key={px} value={String(px)} className="text-xs">{px}px</SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Separator orientation="vertical" className="mx-1 h-5" />

        <ToolButton label="Bold" shortcut={`${MOD}+B`} pressed={state.bold} onClick={() => editor.chain().focus().toggleBold().run()}>
          <BoldIcon className="h-3.5 w-3.5" />
        </ToolButton>
        <ToolButton label="Italic" shortcut={`${MOD}+I`} pressed={state.italic} onClick={() => editor.chain().focus().toggleItalic().run()}>
          <ItalicIcon className="h-3.5 w-3.5" />
        </ToolButton>
        <LinkPopover editor={editor} open={linkOpen} onOpenChange={setLinkOpen} anchor={linkButton} />
        <EmojiButton
          keepOpen
          onOpenChange={setEmojiOpen}
          className="h-7 w-7 text-muted-foreground"
          onPick={(emoji) => editor.chain().focus().insertContent(emoji).run()}
        />
        <Separator orientation="vertical" className="mx-1 h-5" />
        <ToolButton label="Clear formatting" onClick={() => editor.chain().focus().unsetAllMarks().run()}>
          <RemoveFormatting className="h-3.5 w-3.5" />
        </ToolButton>
      </div>

      <EditorContent editor={editor} />

      {((state.link && active) || (max != null && (active || overLimit))) && (
        <div className="flex items-center gap-2 border-t bg-muted/20 px-2 py-1">
          {state.link && active && (
            <div className="flex min-w-0 flex-1 items-center gap-1 text-xs">
              <Link2 className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />
              <a href={state.href} target="_blank" rel="noopener noreferrer" className="truncate text-primary underline underline-offset-2">
                {state.href}
              </a>
              <Button type="button" variant="ghost" size="sm" className="h-6 shrink-0 px-1.5 text-xs" onMouseDown={(e) => e.preventDefault()} onClick={() => setLinkOpen(true)}>
                <Pencil className="mr-1 h-3 w-3" /> Edit
              </Button>
              <Button type="button" variant="ghost" size="sm" className="h-6 shrink-0 px-1.5 text-xs" onClick={() => window.open(state.href, '_blank', 'noopener,noreferrer')}>
                <ExternalLink className="mr-1 h-3 w-3" /> Open
              </Button>
              <Button
                type="button"
                variant="ghost"
                size="sm"
                className="h-6 shrink-0 px-1.5 text-xs text-destructive hover:text-destructive"
                onMouseDown={(e) => e.preventDefault()}
                onClick={() => editor.chain().focus().extendMarkRange('link').unsetLink().run()}
              >
                <Unlink className="mr-1 h-3 w-3" /> Remove
              </Button>
            </div>
          )}
          {max != null && (
            <span className={cn('ml-auto shrink-0 text-[11px] tabular-nums', overLimit ? 'text-destructive' : 'text-muted-foreground')}>
              {length}/{max}
            </span>
          )}
        </div>
      )}
    </div>
  );
}
